import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ChartCanvas from "@/components/chart/ChartCanvas";
import OutlineView from "@/components/chart/OutlineView";
import TeamGrid from "@/components/chart/TeamGrid";
import ProfilePanel from "@/components/chart/ProfilePanel";
import Legend from "@/components/chart/Legend";
import { ViewSettingsDialog } from "@/components/chart/ViewSettingsDialog";
import { ShareDialog } from "@/components/chart/ShareDialog";
import { ChartToolbar } from "@/components/chart/ChartToolbar";
import { ConfirmMoveDialog } from "@/components/chart/ConfirmMoveDialog";
import { EmployeeDialog } from "@/components/EmployeeDialog";
import { buildTree } from "@/components/chart/layouts";
import { exportChart } from "@/lib/chartExport";
import { Network, MoveHorizontal, List, Orbit, LayoutGrid, UserPlus, FileUp, Sparkles } from "lucide-react";

const LAYOUTS = [
  { key: "classic", label: "Classic Tree", icon: Network },
  { key: "horizontal", label: "Horizontal Tree", icon: MoveHorizontal },
  { key: "outline", label: "Compact Outline", icon: List },
  { key: "radial", label: "Radial", icon: Orbit },
  { key: "team_grid", label: "Team Grid", icon: LayoutGrid },
];

export default function ChartPage() {
  const { user } = useAuth();
  const { viewId } = useParams();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [views, setViews] = useState([]);
  const [layout, setLayout] = useState("classic");
  const [collapsed, setCollapsed] = useState(new Set());
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [focusNode, setFocusNode] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [confirmMove, setConfirmMove] = useState(null);
  const [addPerson, setAddPerson] = useState(null); // null | {presetManager}
  const [exporting, setExporting] = useState(false);
  const [exportMenu, setExportMenu] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const undoStack = useRef([]);
  const canvasRef = useRef(null);

  const isViewer = user?.role === "viewer";

  const load = useCallback(async () => {
    const [e, v] = await Promise.all([api.get(`/employees?chart_id=${viewId}`), api.get("/views")]);
    setEmployees(e.data);
    setViews(v.data);
    setLoaded(true);
    return v.data;
  }, [viewId]);

  useEffect(() => {
    setCollapsed(new Set());
    setSelected(null);
    undoStack.current = [];
    load().then((vs) => {
      const v = vs.find((x) => x.id === viewId);
      if (v) setLayout(v.default_layout || "classic");
    }).catch((e) => toast.error(errMsg(e)));
  }, [load, viewId]);

  const view = views.find((v) => v.id === viewId);
  const canReparent = !!view?.can_edit && !isViewer;
  const activeCount = employees.filter((e) => e.status === "active").length;

  const results = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return employees.filter((e) => e.status === "active" &&
      [e.full_name, e.designation, e.department, e.employee_id].some((f) => f?.toLowerCase().includes(q))
    ).slice(0, 8);
  }, [query, employees]);

  const onToggle = useCallback((id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const onSelect = useCallback((emp) => setSelected(emp), []);

  const doMove = async (empId, targetEmpId, record = true) => {
    const emp = employees.find((e) => e.id === empId);
    const target = employees.find((e) => e.id === targetEmpId);
    if (!emp) return;
    try {
      await api.put(`/employees/${empId}/manager`, { manager_employee_id: target ? target.employee_id : targetEmpId });
      if (record) {
        undoStack.current.push({ empId, oldMgr: emp.manager_employee_id });
        if (undoStack.current.length > 10) undoStack.current.shift();
      }
      toast.success(`${emp.full_name} moved${target ? ` under ${target.full_name}` : ""}`);
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const onMove = useCallback((empId, targetId) => {
    const emp = employees.find((e) => e.id === empId);
    const target = employees.find((e) => e.id === targetId);
    if (!emp || !target) return;
    const { childrenOf } = buildTree(employees, null);
    let count = 0;
    const stack = [...(childrenOf[emp.employee_id] || [])];
    while (stack.length) { count++; const c = stack.pop(); stack.push(...(childrenOf[c.employee_id] || [])); }
    setConfirmMove({ emp, target, count });
  }, [employees]);

  const undo = async () => {
    const last = undoStack.current.pop();
    if (!last) { toast.info("Nothing to undo"); return; }
    const emp = employees.find((e) => e.id === last.empId);
    try {
      await api.put(`/employees/${last.empId}/manager`, { manager_employee_id: last.oldMgr });
      toast.success(`Undid move of ${emp?.full_name || "employee"}`);
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  useEffect(() => {
    const h = (e) => { if ((e.ctrlKey || e.metaKey) && e.key === "z" && canReparent) { e.preventDefault(); undo(); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });

  const onExport = async (format) => {
    setExportMenu(false);
    setExporting(true);
    try {
      await exportChart(canvasRef.current, format, view?.name);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error("Export failed: " + e.message);
    } finally {
      setExporting(false);
    }
  };

  const collapseToLevel = (n) => {
    const { roots, childrenOf } = buildTree(employees, view?.root_employee_id);
    const next = new Set();
    const walk = (emp, depth) => {
      const kids = childrenOf[emp.employee_id] || [];
      if (depth >= n && kids.length) next.add(emp.id);
      kids.forEach((k) => walk(k, depth + 1));
    };
    roots.forEach((r) => walk(r, 0));
    setCollapsed(next);
  };

  if (!loaded) {
    return <div className="h-full flex items-center justify-center"><div className="w-8 h-8 rounded-full border-4 border-[#6161FF] border-t-transparent animate-spin" /></div>;
  }
  if (!view) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3 text-center p-8">
        <Network size={40} className="text-[#C4C7D4]" />
        <h2 className="text-2xl font-black tracking-tight">Chart not found</h2>
        <Link to="/" className="text-sm font-bold text-[#6161FF] hover:underline">← Back to all charts</Link>
      </div>
    );
  }

  return (
    <div className="h-full relative flex flex-col" data-testid="chart-page">
      <ChartToolbar view={view} viewId={viewId} views={views} query={query} setQuery={setQuery}
        results={results} onPickResult={(r) => { setFocusNode({ id: r.id, ts: Date.now() }); setQuery(""); setCollapsed(new Set()); }}
        activeCount={activeCount} layout={layout}
        onExpandAll={() => setCollapsed(new Set())} onCollapseLevel2={() => collapseToLevel(2)}
        canReparent={canReparent} onUndo={undo}
        exporting={exporting} exportMenu={exportMenu} setExportMenu={setExportMenu} onExport={onExport}
        onAddPerson={() => setAddPerson({})} onOpenSettings={() => setShowViewDialog("edit")} onOpenShare={() => setShowShare(true)} />

      <div className="flex-1 relative overflow-hidden" ref={canvasRef}>
        {activeCount === 0 ? (
          <div className="h-full flex flex-col items-center justify-center gap-4 text-center p-8 organa-grain" data-testid="empty-chart-state">
            <span className="w-16 h-16 rounded-2xl bg-[#6161FF]/10 flex items-center justify-center"><Sparkles size={28} className="text-[#6161FF]" /></span>
            <h2 className="text-2xl font-black tracking-tight">This chart is empty</h2>
            <p className="text-[#676a82] font-medium max-w-sm">Start mapping your team — add your first person or import a whole list at once.</p>
            {view.can_edit && (
              <div className="flex gap-3">
                <button data-testid="empty-add-person-btn" onClick={() => setAddPerson({})}
                  className="flex items-center gap-2 px-5 h-11 rounded-xl bg-[#6161FF] hover:bg-[#5050E5] text-white font-bold">
                  <UserPlus size={16} /> Add first person
                </button>
                <button data-testid="empty-import-btn" onClick={() => navigate(`/imports?chart=${view.id}`)}
                  className="flex items-center gap-2 px-5 h-11 rounded-xl bg-white border-2 border-[#181B34]/10 font-bold text-[#4b4e63] hover:border-[#6161FF]">
                  <FileUp size={16} /> Import a file
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            {(layout === "classic" || layout === "horizontal" || layout === "radial") && (
              <ChartCanvas employees={employees} view={view} layout={layout} collapsed={collapsed}
                onToggle={onToggle} onSelect={onSelect} onMove={onMove} canReparent={canReparent} focusNode={focusNode} />
            )}
            {layout === "outline" && <OutlineView employees={employees} view={view} onSelect={onSelect} focusNode={focusNode} />}
            {layout === "team_grid" && <TeamGrid employees={employees} view={view} onSelect={onSelect} focusNode={focusNode} />}

            <Legend employees={employees} colorConfig={view.color_config} />

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1 p-1.5 rounded-2xl bg-white/85 backdrop-blur-xl border border-white/50 shadow-xl" data-testid="layout-switcher">
              {LAYOUTS.map((l) => (
                <button key={l.key} data-testid={`layout-${l.key}`} aria-label={l.label} title={l.label}
                  onClick={() => setLayout(l.key)}
                  className={`flex items-center gap-1.5 px-3 h-9 rounded-xl text-xs font-bold ${layout === l.key ? "bg-[#6161FF] text-white shadow-md shadow-[#6161FF]/30" : "text-[#4b4e63] hover:bg-[#F0F3FF]"}`}>
                  <l.icon size={15} />
                  <span className="hidden xl:inline">{l.label}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {selected && (
          <ProfilePanel emp={employees.find((e) => e.id === selected.id) || selected} employees={employees} view={view}
            onClose={() => setSelected(null)} onChanged={load} canEdit={view.can_edit && !isViewer} canReparent={canReparent}
            onAddReport={(mgr) => { setSelected(null); setAddPerson({ presetManager: mgr.employee_id }); }}
            onEditPerson={(emp) => { setSelected(null); setAddPerson({ edit: emp }); }} />
        )}
      </div>

      {confirmMove && (
        <ConfirmMoveDialog confirmMove={confirmMove} onCancel={() => setConfirmMove(null)}
          onConfirm={() => { doMove(confirmMove.emp.id, confirmMove.target.id); setConfirmMove(null); }} />
      )}

      {addPerson && (
        <EmployeeDialog emp={addPerson.edit || null} chartId={view.id} chartEmployees={employees} presetManager={addPerson.presetManager}
          onClose={() => setAddPerson(null)} onSaved={() => { setAddPerson(null); load(); }} />
      )}
      {showViewDialog && (
        <ViewSettingsDialog isNew={false} view={view} employees={employees} currentUser={user}
          onClose={() => setShowViewDialog(null)}
          onSaved={() => { setShowViewDialog(null); load(); }}
          onDeleted={() => { setShowViewDialog(null); navigate("/"); }} />
      )}
      {showShare && <ShareDialog view={view} onClose={() => setShowShare(false)} onSaved={() => load()} />}
    </div>
  );
}
